import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Swords, ClipboardList, Send, Settings } from "lucide-react";
import { api, extractApiError } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useI18n } from "@/contexts/I18nContext";

function OverviewCard({ label, value, icon: Icon }) {
  return (
    <Card className="border-border">
      <CardContent className="p-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-accent/10 text-accent flex items-center justify-center">
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className="text-2xl font-display font-extrabold">{value}</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Admin() {
  const { t } = useI18n();
  const [ov, setOv] = useState({});
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [games, setGames] = useState([]);
  const [logs, setLogs] = useState([]);
  const [annTitle, setAnnTitle] = useState("");
  const [annBody, setAnnBody] = useState("");
  const [settings, setSettings] = useState({ turn_seconds: 40, maintenance: false });

  const loadAll = async () => {
    const [o, u, g, l, s] = await Promise.all([
      api.get("/admin/overview"), api.get("/admin/users"),
      api.get("/admin/games"), api.get("/admin/logs"),
      api.get("/admin/settings"),
    ]);
    setOv(o.data); setUsers(u.data); setGames(g.data); setLogs(l.data); setSettings(s.data);
  };
  useEffect(() => { loadAll(); }, []);

  const searchUsers = async () => {
    const { data } = await api.get("/admin/users", { params: { q } });
    setUsers(data);
  };

  const setRole = async (uid, role) => {
    try {
      await api.patch(`/admin/users/${uid}`, { role });
      toast.success(t("common.success"));
      loadAll();
    } catch (e) { toast.error(extractApiError(e)); }
  };

  const deleteUser = async (uid) => {
    if (!window.confirm(`Supprimer l'utilisateur ${uid} ?`)) return;
    try { await api.delete(`/admin/users/${uid}`); loadAll(); toast.success(t("common.success")); }
    catch (e) { toast.error(extractApiError(e)); }
  };

  const deleteGame = async (gid) => {
    if (!window.confirm("Supprimer cette partie ?")) return;
    try { await api.delete(`/admin/games/${gid}`); loadAll(); toast.success(t("common.success")); }
    catch (e) { toast.error(extractApiError(e)); }
  };

  const sendAnn = async () => {
    if (!annTitle || !annBody) return;
    try {
      const { data } = await api.post("/admin/announcement", { title: annTitle, body: annBody });
      toast.success(`${t("common.success")}: ${data.count}`);
      setAnnTitle(""); setAnnBody("");
    } catch (e) { toast.error(extractApiError(e)); }
  };

  const saveSettings = async () => {
    try { await api.patch("/admin/settings", settings); toast.success(t("common.success")); }
    catch (e) { toast.error(extractApiError(e)); }
  };

  return (
    <AppLayout>
      <div className="flex items-center gap-3 mb-6">
        <Settings className="w-8 h-8 text-primary" />
        <h1 className="font-display font-extrabold text-4xl tracking-tight">{t("admin.title")}</h1>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <OverviewCard label={t("admin.users")} value={ov.users || 0} icon={Users} />
        <OverviewCard label="Admins" value={ov.admins || 0} icon={Users} />
        <OverviewCard label={t("admin.games")} value={ov.games || 0} icon={Swords} />
        <OverviewCard label="Actives" value={ov.active_games || 0} icon={Swords} />
        <OverviewCard label="Terminées" value={ov.finished_games || 0} icon={Swords} />
      </div>

      <Tabs defaultValue="users" data-testid="admin-tabs">
        <TabsList>
          <TabsTrigger value="users" data-testid="admin-tab-users">{t("admin.users")}</TabsTrigger>
          <TabsTrigger value="games" data-testid="admin-tab-games">{t("admin.games")}</TabsTrigger>
          <TabsTrigger value="logs" data-testid="admin-tab-logs">{t("admin.logs")}</TabsTrigger>
          <TabsTrigger value="announcement" data-testid="admin-tab-ann">{t("admin.announcement")}</TabsTrigger>
          <TabsTrigger value="settings" data-testid="admin-tab-settings">{t("admin.settings")}</TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="mt-4">
          <div className="flex gap-2 mb-3">
            <Input placeholder={t("common.search")} value={q} onChange={(e) => setQ(e.target.value)}
              className="max-w-md" data-testid="admin-user-search" />
            <Button onClick={searchUsers} data-testid="admin-user-search-btn">{t("common.search")}</Button>
          </div>
          <Card className="border-border">
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="text-left px-4 py-2">Email</th>
                    <th className="text-left px-4 py-2">Nom</th>
                    <th className="text-left px-4 py-2">Rôle</th>
                    <th className="text-left px-4 py-2">Org</th>
                    <th className="text-right px-4 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.user_id} className="border-t border-border" data-testid={`admin-user-${u.user_id}`}>
                      <td className="px-4 py-2">{u.email}</td>
                      <td className="px-4 py-2">{u.name}</td>
                      <td className="px-4 py-2">
                        <Select value={u.role} onValueChange={(v) => setRole(u.user_id, v)}>
                          <SelectTrigger className="h-8 w-28"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="user">user</SelectItem>
                            <SelectItem value="admin">admin</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-2 text-muted-foreground">{u.organization || "—"}</td>
                      <td className="px-4 py-2 text-right">
                        <Button size="sm" variant="destructive" onClick={() => deleteUser(u.user_id)}
                          data-testid={`admin-user-delete-${u.user_id}`}>
                          {t("common.delete")}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="games" className="mt-4">
          <Card className="border-border">
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="text-left px-4 py-2">ID</th>
                    <th className="text-left px-4 py-2">Statut</th>
                    <th className="text-left px-4 py-2">Joueurs</th>
                    <th className="text-left px-4 py-2">Créée</th>
                    <th className="text-right px-4 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {games.map((g) => (
                    <tr key={g.game_id} className="border-t border-border">
                      <td className="px-4 py-2 font-mono text-xs">{g.game_id}</td>
                      <td className="px-4 py-2"><Badge variant="outline">{g.status}</Badge></td>
                      <td className="px-4 py-2">{(g.players || []).map((p) => p.name).join(" vs ") || "—"}</td>
                      <td className="px-4 py-2 text-xs text-muted-foreground">{new Date(g.created_at).toLocaleString()}</td>
                      <td className="px-4 py-2 text-right">
                        <Button size="sm" variant="destructive" onClick={() => deleteGame(g.game_id)}>
                          {t("common.delete")}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="logs" className="mt-4">
          <Card className="border-border">
            <CardContent className="p-0">
              <div className="divide-y divide-border">
                {logs.map((l, i) => (
                  <div key={i} className="px-4 py-2 text-sm font-mono flex items-center gap-3">
                    <span className="text-xs text-muted-foreground">{new Date(l.at).toLocaleString()}</span>
                    <Badge variant="outline">{l.action}</Badge>
                    <span className="text-muted-foreground">{l.target || ""}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="announcement" className="mt-4">
          <Card className="border-border max-w-xl">
            <CardHeader><CardTitle className="font-display">{t("admin.broadcast")}</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>{t("admin.title.field")}</Label>
                <Input value={annTitle} onChange={(e) => setAnnTitle(e.target.value)} className="h-11 mt-1"
                  data-testid="admin-ann-title" />
              </div>
              <div>
                <Label>{t("admin.body.field")}</Label>
                <Textarea value={annBody} onChange={(e) => setAnnBody(e.target.value)} rows={4}
                  data-testid="admin-ann-body" />
              </div>
              <Button onClick={sendAnn} data-testid="admin-ann-send-btn">
                <Send className="w-4 h-4 mr-1" /> {t("admin.send")}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings" className="mt-4">
          <Card className="border-border max-w-xl">
            <CardHeader><CardTitle className="font-display">{t("admin.settings")}</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>{t("admin.turnSeconds")}</Label>
                <Input type="number" value={settings.turn_seconds || 40}
                  onChange={(e) => setSettings({ ...settings, turn_seconds: parseInt(e.target.value) || 40 })}
                  className="h-11 mt-1" data-testid="admin-turnsec-input" />
              </div>
              <Button onClick={saveSettings} data-testid="admin-settings-save-btn">
                {t("admin.save")}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </AppLayout>
  );
}
